import { ElectionSummary } from '../types';

interface SiteMetaOptions {
  election?: Partial<ElectionSummary> | null;
  pageTitle?: string;
  customMetaTitle?: string;
  description?: string;
  image?: string;
  keywords?: string;
}

function getOrCreateMetaTag(nameOrProperty: string, isProperty = false): HTMLMetaElement {
  const attr = isProperty ? 'property' : 'name';
  let tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${nameOrProperty}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, nameOrProperty);
    document.head.appendChild(tag);
  }
  return tag;
}

/**
 * Centrally updates document.title and all OpenGraph / Twitter / SEO metadata tags.
 */
export function setSiteMeta(opts: SiteMetaOptions = {}): void {
  if (typeof document === 'undefined') return;

  const { election, pageTitle, customMetaTitle, description, image, keywords } = opts;

  // 1. Resolve Title
  let resolvedTitle = 'Electpoll - Secure Online Voting Platform';
  const customTitle = customMetaTitle || election?.metaTitle;
  const electTitle = election?.title;

  if (pageTitle) {
    if (customTitle) {
      resolvedTitle = `${pageTitle} | ${customTitle}`;
    } else if (electTitle) {
      resolvedTitle = `${pageTitle} | ${electTitle} - Electpoll`;
    } else {
      resolvedTitle = `${pageTitle} - Electpoll`;
    }
  } else {
    if (customTitle) {
      resolvedTitle = customTitle;
    } else if (electTitle) {
      resolvedTitle = `${electTitle} - Electpoll`;
    }
  }

  document.title = resolvedTitle;

  // 2. Resolve Description
  const resolvedDesc =
    description ||
    election?.metaDescription ||
    election?.description ||
    'Electpoll is a secure, transparent, and user-friendly digital voting platform designed for modern democratic processes.';

  // 3. Resolve Image
  const resolvedImage =
    image ||
    election?.ogImageUrl ||
    election?.logo1Url ||
    (typeof window !== 'undefined' ? `${window.location.origin}/assets/images/favicon.png` : '/assets/images/favicon.png');

  // 4. Resolve Keywords
  const resolvedKeywords =
    keywords ||
    election?.metaKeywords ||
    'electpoll, election, elections, online voting, platform, electronic voting, e-voting';

  // 5. Update Standard Meta Tags
  getOrCreateMetaTag('description').content = resolvedDesc;
  getOrCreateMetaTag('keywords').content = resolvedKeywords;
  getOrCreateMetaTag('author').content = election?.organizationName || 'Electpoll';

  // 6. Update OpenGraph Tags
  getOrCreateMetaTag('og:title', true).content = resolvedTitle;
  getOrCreateMetaTag('og:description', true).content = resolvedDesc;
  getOrCreateMetaTag('og:image', true).content = resolvedImage;
  getOrCreateMetaTag('og:type', true).content = 'website';
  getOrCreateMetaTag('og:site_name', true).content = election?.organizationName || 'Electpoll';
  if (typeof window !== 'undefined') {
    getOrCreateMetaTag('og:url', true).content = window.location.href;
  }

  // 7. Update Twitter Card Tags
  getOrCreateMetaTag('twitter:card').content = 'summary_large_image';
  getOrCreateMetaTag('twitter:title').content = resolvedTitle;
  getOrCreateMetaTag('twitter:description').content = resolvedDesc;
  getOrCreateMetaTag('twitter:image').content = resolvedImage;
}
